/**
 * The cross-page "Move to Rehab" action, shared by the pipeline kanban and the
 * Properties page.
 *
 * It inserts a `rehab_projects` row (idempotent — reuses an existing one),
 * claims any Find-page scope rows under the new project, invalidates every
 * query that keys off rehab membership, and navigates to /rehab. A property
 * that's actively listed for rent is blocked with `RentalListingBlockedError`
 * so the caller can surface the rental-listing modal.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useWorkspace } from "@/hooks/use-workspace";
import {
  hasActiveRentalListing,
  RentalListingBlockedError,
} from "@/lib/rental-listing-guard";
import { toUserMessage } from "@/lib/supabase-errors";

export function useMoveToRehab() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.id;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (pipelinePropertyId: string) => {
      if (!user?.id || !workspaceId) {
        throw new Error("No active workspace");
      }
      // A property that's actively listed for rent must be removed from
      // Rentals before it can enter rehab.
      if (await hasActiveRentalListing(pipelinePropertyId, workspaceId)) {
        throw new RentalListingBlockedError();
      }
      const { data: existing, error: lookupError } = await supabase
        .from("rehab_projects")
        .select("id")
        .eq("pipeline_property_id", pipelinePropertyId)
        .eq("workspace_id", workspaceId)
        .maybeSingle();
      if (lookupError) throw lookupError;

      let projectId: string | null = existing?.id ?? null;
      if (!projectId) {
        const { data, error } = await supabase
          .from("rehab_projects")
          .insert({
            workspace_id: workspaceId,
            pipeline_property_id: pipelinePropertyId,
            created_by_user_id: user.id,
            // Lands as not_started / in_rehab = false (column defaults). The
            // user explicitly starts it via "Start Rehab" in the Rehab page,
            // which stamps start_date and flips in_rehab true.
          } as never)
          .select("id")
          .single();
        if (error) throw error;
        projectId = (data as { id: string } | null)?.id ?? null;
      }

      // Claim every rehab_line_items row that was built on the Find detail
      // page (rehab_project_id IS NULL) under this project so the Rehab
      // drawer sees them immediately. Idempotent — safe to run for both
      // the freshly-inserted and the already-existing project branches.
      if (projectId) {
        const { error: claimError } = await (
          supabase as unknown as {
            from: (t: string) => {
              update: (v: unknown) => {
                eq: (col: string, v: string) => {
                  is: (col: string, v: null) => Promise<{ error: unknown }>;
                };
              };
            };
          }
        )
          .from("rehab_line_items")
          .update({ rehab_project_id: projectId })
          .eq("pipeline_property_id", pipelinePropertyId)
          .is("rehab_project_id", null);
        if (claimError) {
          console.error("rehab_line_items claim:", claimError);
        }
      }

      return projectId;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["rehab_projects_ids", workspaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["rehab_projects", workspaceId],
      });
      // The property now surfaces on Properties + Financing (both read from
      // useOwnedProperties). Invalidate those keys so they refetch immediately
      // instead of being served stale by TanStack's staleTime.
      void queryClient.invalidateQueries({
        queryKey: ["rehab_projects_finalized", workspaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["rehab_projects_owned", workspaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["pipeline_properties_owned", workspaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["property_settings_owned", workspaceId],
      });
      toast.success("Moved to Rehab");
      navigate("/rehab");
    },
    onError: (err: unknown) => {
      // The "listed for rent" block is surfaced as a modal by the caller
      // (per-call onError) — don't also toast it here.
      if (err instanceof RentalListingBlockedError) return;
      toast.error(toUserMessage(err, "Failed to move to rehab"));
    },
  });
}
